import {Box, Tab, TabList, Tabs} from '@chakra-ui/react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {routes} from 'ff-common';
import {useCadencier, useClient, useDevisVisible} from '@/haligator-factory';
import {useHalCollection, useHalState} from '@synako/haligator';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {TABS_HEIGHT} from '@/constants/layout';
import {usePeutVoirUBEssentielles} from '@/hooks/auth/usePermissionsCommerciales';

// Conteneur Mes Produits: entête à onglets + contenu via Outlet
// Les commentaires doivent être en français selon la convention du projet
export const MesProduitsApp: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Détection disponibilité Opérations commerciales
  const operationCommercialeNav = useClient().follow(
    'operations_commerciales_eligible',
  );
  const operationCommercialeData = useHalCollection(operationCommercialeNav);
  const hasOperationCommerciale = !!(
    operationCommercialeData.items && operationCommercialeData.items.length > 0
  );

  const {buildClientRoute} = useClientRoute();

  // Détection disponibilité Devis personnalisés
  const {devis: devisVisible} = useDevisVisible();

  // Détection disponibilité UB essentielles
  // Le follow est conditionné par la permission pour éviter un fetch sur un nav non-résolvable
  const peutVoirUBEssentielles = usePeutVoirUBEssentielles();
  const clientNav = useClient();
  const profilsUBData = useHalCollection(
    peutVoirUBEssentielles ? clientNav.follow('profils_ub') : undefined,
  );
  const hasProfilsUB = !!(
    profilsUBData.items && profilsUBData.items.length > 0
  );

  const cadencierNav = useCadencier();
  const {state: cadencierState} = useHalState(cadencierNav);
  const cadencierCount = cadencierState?.data.nombre_articles ?? 0;

  // Table des onglets avec leurs routes
  const tabs = [
    {
      path: buildClientRoute(routes.client.cadencier),
      label: t('global.cadencier'),
      count: cadencierCount,
    },
    {
      path: buildClientRoute(routes.client.listes_default),
      label: t('global.listes'),
    },
    {
      path: buildClientRoute(routes.client.mes_produits_par_saison),
      label: t('liste.produits_par_saison'),
    },
    // Opérations commerciales et Devis s'affichent conditionnellement
    ...(hasOperationCommerciale
      ? [
          {
            path: buildClientRoute(
              routes.client.mes_produits_operation_commerciale,
            ),
            label: t('liste.operation_commerciale.titre'),
          },
        ]
      : []),
    ...(peutVoirUBEssentielles && hasProfilsUB
      ? [
          {
            path: buildClientRoute(routes.client.mes_produits_ub_essentielles),
            label: t('global.ub_essentielles'),
          },
        ]
      : []),
    ...(devisVisible
      ? [
          {
            path: buildClientRoute(routes.client.mes_produits_devis),
            label: t('liste.devis_personnalise.titre'),
          },
        ]
      : []),
  ];

  // Calcul de l'onglet courant à partir de l'URL
  const currentIndex = Math.max(
    0,
    tabs.findIndex((tab) => location.pathname.startsWith(tab.path)),
  );

  return (
    <Box
      bg='white'
      minH='100%'
    >
      <Box
        bg={{lg: 'white'}}
        p={{base: '0', md: '0'}}
        pt={{base: '0', md: '0'}}
        minH='100%'
      >
        <Box
          height={TABS_HEIGHT}
          bg='white'
        >
          <Tabs
            index={currentIndex}
            onChange={(index) => {
              // Navigation vers la route de l'onglet sélectionné
              const next = tabs[index];
              // Conserver les paramètres de recherche utiles (ex: oc_id)
              const keep = searchParams.toString();
              const suffix = keep ? `?${keep}` : '';
              navigate(`${next.path}${suffix}`);
            }}
            colorScheme='blue'
          >
            <TabList
              borderBottomWidth='1px'
              px={{base: '2', md: '4', lg: '6'}}
              h='56px'
              overflowX={{base: 'auto', md: 'visible'}}
              overflowY='hidden'
              whiteSpace='nowrap'
              sx={{
                '&::-webkit-scrollbar': {display: 'none'},
                '-ms-overflow-style': 'none',
                'scrollbar-width': 'none',
              }}
            >
              {tabs.map((tab, index) => {
                const isSelected = currentIndex === index;
                return (
                  <Tab
                    key={tab.path}
                    fontSize={{base: 'sm', md: 'md'}}
                    px={{base: '3', md: '4'}}
                    flexShrink={0}
                    whiteSpace='nowrap'
                    // '!important' pour écraser le _selected de Chakra Tabs qui s'applique avec un délai,
                    // ce qui cause un flash visuel entre le badge count et le style de l'onglet
                    sx={{
                      color: isSelected
                        ? 'blue.main !important'
                        : 'gray.500 !important',
                      fontWeight: isSelected
                        ? '700 !important'
                        : '600 !important',
                      borderBottomWidth: isSelected
                        ? '4px !important'
                        : '0 !important',
                      borderColor: isSelected
                        ? 'var(--chakra-colors-blue-main) !important'
                        : 'transparent !important',
                    }}
                  >
                    {tab.label}
                    {tab.count !== undefined && isSelected && (
                      <Box
                        ml='2'
                        textColor='white'
                        fontWeight='600'
                        fontSize='xs'
                        bg='blue.main'
                        p='1'
                        borderRadius='8px'
                      >
                        {tab.count}
                      </Box>
                    )}
                  </Tab>
                );
              })}
            </TabList>
          </Tabs>
        </Box>

        <Box
          bg='white'
          flex='1'
          minH='100%'
        >
          {/* Rendu du contenu de l'onglet via Outlet */}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default MesProduitsApp;
